import React, { useState, useEffect, useCallback, useRef } from 'react';
import AppShell from '../../components/layout/AppShell';
import { financeApi } from '../../services/api';
import api from '../../services/api';
import { fmtPKR, fmtDate, PaymentBadge } from '../../utils/helpers';
import { Spinner, EmptyState, Modal, Pagination } from '../../components/common';
import toast from 'react-hot-toast';
import { Plus, Eye, Printer, Search, Trash2, DollarSign } from 'lucide-react';

function CustomerSearch({ value, onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const debounce = useRef(null);
  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const r = await api.get('/customers?search=' + encodeURIComponent(query) + '&limit=8').then(r => r.data);
        if (r.success) setResults(r.data || []);
      } catch {}
    }, 300);
  }, [query]);
  const pick = (c) => { setSelected(c); setQuery(c.company_name || c.name); setOpen(false); onChange(c.customer_id, c); };
  const clear = () => { setSelected(null); setQuery(''); onChange('', null); setResults([]); };
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--gray-400)', pointerEvents:'none' }} />
        <input className="form-control" style={{ paddingLeft: 32 }} placeholder="Search by name, mobile or company..."
          value={query} onChange={e => { setQuery(e.target.value); setOpen(true); if (!e.target.value) clear(); }}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
        {selected && <button onClick={clear} style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer' }}>x</button>}
      </div>
      {selected && (
        <div style={{ marginTop:6, padding:'8px 12px', background:'var(--gray-50)', borderRadius:8, border:'1px solid var(--gray-200)', fontSize:'.82rem' }}>
          <div style={{ fontWeight:600 }}>{selected.name}</div>
          <div style={{ color:'var(--gray-500)', fontSize:'.72rem' }}>{selected.company_name && selected.company_name + ' · '}{selected.email}</div>
        </div>
      )}
      {open && results.length > 0 && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:1000, background:'white', border:'1px solid var(--gray-200)', borderRadius:8, boxShadow:'0 8px 24px rgba(0,0,0,.12)', overflow:'hidden', marginTop:4 }}>
          {results.map(c => (
            <div key={c.customer_id} style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid var(--gray-100)' }}
              onMouseDown={() => pick(c)} onMouseEnter={e => e.currentTarget.style.background='var(--gray-50)'} onMouseLeave={e => e.currentTarget.style.background='white'}>
              <div style={{ fontWeight:600, fontSize:'.85rem' }}>{c.company_name || c.name}</div>
              <div style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>{c.email} · {c.mobile}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LineRow({ line, index, onChange, onRemove }) {
  const net = parseFloat(line.unitPrice||0) * parseInt(line.quantity||1);
  const disc = net * (parseFloat(line.discountPct||0)/100);
  const tax = (net-disc) * (parseFloat(line.taxPct||0)/100);
  const total = net - disc + tax;
  return (
    <tr>
      <td style={{ padding:'5px 6px', border:'1px solid var(--gray-200)' }}>
        <input style={{ width:'100%', border:'none', fontSize:'.82rem', padding:'2px 4px', background:'transparent' }} placeholder="Description" value={line.description} onChange={e => onChange(index, 'description', e.target.value)} />
      </td>
      <td style={{ padding:'5px 4px', border:'1px solid var(--gray-200)', width:64 }}>
        <input type="number" min="1" style={{ width:'100%', border:'none', textAlign:'center', fontSize:'.82rem', background:'transparent' }} value={line.quantity} onChange={e => onChange(index, 'quantity', e.target.value)} />
      </td>
      <td style={{ padding:'5px 4px', border:'1px solid var(--gray-200)', width:110 }}>
        <input type="number" step="0.01" min="0" placeholder="0.00" style={{ width:'100%', border:'none', textAlign:'right', fontSize:'.82rem', background:'transparent' }} value={line.unitPrice} onChange={e => onChange(index, 'unitPrice', e.target.value)} />
      </td>
      <td style={{ padding:'5px 4px', border:'1px solid var(--gray-200)', width:62 }}>
        <input type="number" step="0.01" min="0" max="100" placeholder="0" style={{ width:'100%', border:'none', textAlign:'center', fontSize:'.82rem', background:'transparent' }} value={line.discountPct} onChange={e => onChange(index, 'discountPct', e.target.value)} />
      </td>
      <td style={{ padding:'5px 4px', border:'1px solid var(--gray-200)', width:62 }}>
        <input type="number" step="0.01" min="0" max="100" placeholder="17" style={{ width:'100%', border:'none', textAlign:'center', fontSize:'.82rem', background:'transparent' }} value={line.taxPct} onChange={e => onChange(index, 'taxPct', e.target.value)} />
      </td>
      <td style={{ padding:'5px 8px', border:'1px solid var(--gray-200)', textAlign:'right', fontWeight:600, fontSize:'.82rem', color: total>0?'var(--navy)':'var(--gray-300)', minWidth:100 }}>
        {total > 0 ? fmtPKR(total) : '-'}
      </td>
      <td style={{ padding:'4px', border:'1px solid var(--gray-200)', textAlign:'center' }}>
        <button onClick={() => onRemove(index)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--danger)', padding:3 }}><Trash2 size={13}/></button>
      </td>
    </tr>
  );
}

function InvoiceTotals({ lines }) {
  let subtotal=0, taxTotal=0;
  lines.forEach(l => {
    const net = parseFloat(l.unitPrice||0) * parseInt(l.quantity||1);
    const disc = net * (parseFloat(l.discountPct||0)/100);
    const tax = (net-disc) * (parseFloat(l.taxPct||0)/100);
    subtotal += net-disc; taxTotal += tax;
  });
  return (
    <div style={{ maxWidth:260, marginLeft:'auto', marginTop:6, borderTop:'2px solid var(--gray-200)', paddingTop:8 }}>
      <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:'.8rem' }}><span>Subtotal</span><span>{fmtPKR(subtotal)}</span></div>
      <div style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:'.8rem' }}><span>Tax / GST</span><span>{fmtPKR(taxTotal)}</span></div>
      <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0 4px', fontWeight:700, fontSize:'1rem', color:'var(--navy)', borderTop:'2px solid var(--navy)', marginTop:4 }}><span>Total Due</span><span>{fmtPKR(subtotal+taxTotal)}</span></div>
    </div>
  );
}

function MarkPaidModal({ invoice, onClose, onDone }) {
  const [form, setForm] = useState({ amount: invoice.total||'', method:'BANK_TRANSFER', reference:'', paidDate: new Date().toISOString().slice(0,10) });
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    setSaving(true);
    try {
      await financeApi.markPaid(invoice.invoice_id, { paidAmount: parseFloat(form.amount) });
      if (form.reference) {
        await api.post('/finance/vouchers', {
          voucherType:'BRV', amount:parseFloat(form.amount),
          description:'Payment for ' + invoice.invoice_no,
          referenceNo:form.reference, partyName:invoice.customer_name,
          partyType:'customer', voucherDate:form.paidDate,
          lines:[
            { accountCode:'1020', debit:parseFloat(form.amount), credit:0, description:'Receipt ' + invoice.invoice_no },
            { accountCode:'1040', debit:0, credit:parseFloat(form.amount), description:'AR clearance ' + invoice.invoice_no },
          ],
        }).catch(()=>{});
      }
      toast.success('Payment recorded'); onDone();
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title={'Record Payment'}>
      <div className="modal-body">
        <div style={{ padding:'10px 14px', background:'var(--gray-50)', borderRadius:8, marginBottom:14, fontSize:'.85rem' }}>
          <div style={{ fontWeight:600 }}>{invoice.customer_name}</div>
          <div style={{ color:'var(--gray-500)', marginTop:3 }}>Total: <strong>{fmtPKR(invoice.total)}</strong></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Amount (PKR)</label><input className="form-control" type="number" step="0.01" value={form.amount} onChange={set('amount')} /></div>
          <div className="form-group"><label className="form-label">Payment Date</label><input className="form-control" type="date" value={form.paidDate} onChange={set('paidDate')} /></div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Method</label>
            <select className="form-control" value={form.method} onChange={set('method')}>
              {['BANK_TRANSFER','CHEQUE','CASH','ONLINE','CREDIT'].map(m=><option key={m} value={m}>{m.replace('_',' ')}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Reference No</label><input className="form-control" placeholder="Cheque / transaction ref" value={form.reference} onChange={set('reference')} /></div>
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-success" onClick={submit} disabled={saving}><DollarSign size={14}/> {saving?'Saving...':'Record Payment'}</button>
      </div>
    </Modal>
  );
}

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status:'', fromDate:'', toDate:'' });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showPay, setShowPay] = useState(null);
  const [creating, setCreating] = useState(false);
  const blankForm = () => ({ customerId:'', customerObj:null, billingPeriodStart:'', billingPeriodEnd:'', dueDate:'', notes:'', paymentTerms:'NET30', lines:[{ description:'', quantity:1, unitPrice:'', discountPct:0, taxPct:17 }] });
  const [createForm, setCreateForm] = useState(blankForm());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await financeApi.getInvoices({ page, limit:20, status:filters.status||undefined, fromDate:filters.fromDate||undefined, toDate:filters.toDate||undefined });
      if (res.success) { setInvoices(res.data); setPagination(res.pagination||{}); }
    } finally { setLoading(false); }
  }, [page, filters]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (inv) => {
    setSelected(inv); setDetail(null);
    const r = await financeApi.getInvoice(inv.invoice_id);
    if (r.success) setDetail(r.data);
  };

  const handleCreate = async () => {
    const lines = createForm.lines.filter(l => l.description && parseFloat(l.unitPrice) > 0);
    if (!createForm.customerId) { toast.error('Select a customer'); return; }
    if (!lines.length && !createForm.billingPeriodStart) { toast.error('Add line items or set a billing period'); return; }
    let dueDate = createForm.dueDate;
    if (!dueDate && createForm.paymentTerms !== 'IMMEDIATE') {
      const days = parseInt(createForm.paymentTerms.replace('NET',''))||30;
      const d = new Date(); d.setDate(d.getDate()+days);
      dueDate = d.toISOString().slice(0,10);
    }
    setCreating(true);
    try {
      const res = await financeApi.createInvoice({ customerId:createForm.customerId, billingPeriodStart:createForm.billingPeriodStart||undefined, billingPeriodEnd:createForm.billingPeriodEnd||undefined, dueDate:dueDate||undefined, notes:createForm.notes||undefined, lineItems:lines.map(l=>({ description:l.description, quantity:parseInt(l.quantity)||1, unitPrice:parseFloat(l.unitPrice), discountPct:parseFloat(l.discountPct||0), taxPct:parseFloat(l.taxPct||0) })) });
      if (res.success) { toast.success('Invoice ' + res.data.invoice_no + ' created'); setShowCreate(false); setCreateForm(blankForm()); load(); }
      else throw new Error(res.message);
    } catch(e) { toast.error(e.message||'Failed'); }
    finally { setCreating(false); }
  };

  const addLine = () => setCreateForm(f => ({ ...f, lines:[...f.lines,{description:'',quantity:1,unitPrice:'',discountPct:0,taxPct:17}] }));
  const setLine = (i,k,v) => setCreateForm(f => ({ ...f, lines:f.lines.map((l,j)=>j===i?{...l,[k]:v}:l) }));
  const removeLine = (i) => { if(createForm.lines.length<=1) return; setCreateForm(f => ({ ...f, lines:f.lines.filter((_,j)=>j!==i) })); };

  const stats = invoices.reduce((s,inv)=>({ pending:s.pending+(inv.payment_status==='PENDING'?parseFloat(inv.total||0):0), overdue:s.overdue+(inv.payment_status==='OVERDUE'?parseFloat(inv.total||0):0), paid:s.paid+(inv.payment_status==='PAID'?parseFloat(inv.paid_amount||0):0) }),{pending:0,overdue:0,paid:0});

  return (
    <AppShell title="Invoices" actions={<button className="btn btn-primary btn-sm" onClick={()=>setShowCreate(true)}><Plus size={14}/> Create Invoice</button>}>
      <div className="stat-grid" style={{ gridTemplateColumns:'repeat(3,1fr)', marginBottom:20 }}>
        <div className="stat-card"><div className="stat-label">Pending</div><div className="stat-value" style={{ color:'var(--warning)', fontSize:'1.1rem' }}>{fmtPKR(stats.pending)}</div></div>
        <div className="stat-card"><div className="stat-label">Overdue</div><div className="stat-value" style={{ color:'var(--danger)', fontSize:'1.1rem' }}>{fmtPKR(stats.overdue)}</div></div>
        <div className="stat-card"><div className="stat-label">Collected</div><div className="stat-value" style={{ color:'var(--success)', fontSize:'1.1rem' }}>{fmtPKR(stats.paid)}</div></div>
      </div>
      <div className="filters-bar">
        <select className="form-control" style={{ width:160 }} value={filters.status} onChange={e=>setFilters(f=>({...f,status:e.target.value}))}>
          <option value="">All Statuses</option>
          {['PENDING','PAID','OVERDUE','CANCELLED'].map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <input className="form-control" type="date" style={{ width:150 }} value={filters.fromDate} onChange={e=>setFilters(f=>({...f,fromDate:e.target.value}))} />
        <input className="form-control" type="date" style={{ width:150 }} value={filters.toDate} onChange={e=>setFilters(f=>({...f,toDate:e.target.value}))} />
        <button className="btn btn-outline btn-sm" onClick={load}>Refresh</button>
      </div>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : invoices.length ? (
            <table>
              <thead><tr><th>Invoice No</th><th>Customer</th><th>Period</th><th>Subtotal</th><th>Tax</th><th>Total</th><th>Due Date</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {invoices.map(inv=>(
                  <tr key={inv.invoice_id} style={{ background:inv.payment_status==='OVERDUE'?'#FEF2F2':undefined }}>
                    <td><code style={{ fontSize:'.78rem', color:'var(--navy)', fontWeight:700 }}>{inv.invoice_no}</code></td>
                    <td><div style={{ fontWeight:500 }}>{inv.customer_name}</div>{inv.company_name&&<div className="text-sm text-muted">{inv.company_name}</div>}</td>
                    <td className="text-sm">{inv.billing_period_start ? fmtDate(inv.billing_period_start)+' - '+fmtDate(inv.billing_period_end) : '-'}</td>
                    <td>{fmtPKR(inv.subtotal)}</td>
                    <td className="text-sm">{fmtPKR(inv.tax_amount)}</td>
                    <td style={{ fontWeight:700, color:'var(--navy)' }}>{fmtPKR(inv.total)}</td>
                    <td className="text-sm">{inv.due_date?fmtDate(inv.due_date):'-'}</td>
                    <td><PaymentBadge status={inv.payment_status} /></td>
                    <td>
                      <div style={{ display:'flex', gap:4 }}>
                        <button className="btn btn-outline btn-sm" onClick={()=>openDetail(inv)}><Eye size={12}/></button>
                        {['PENDING','OVERDUE'].includes(inv.payment_status) && (
                          <button className="btn btn-outline btn-sm" title="Edit due date"
                            onClick={()=>{ const d=prompt('New due date (YYYY-MM-DD):',inv.due_date?inv.due_date.slice(0,10):''); if(d){ api.patch('/invoices/'+inv.invoice_id,{ dueDate:d }).then(()=>{ toast.success('Due date updated'); load(); }).catch(()=>toast.error('Failed')); } }}>
                            ✏️
                          </button>
                        )}
                        {(inv.payment_status==='PENDING'||inv.payment_status==='OVERDUE')&&<button className="btn btn-success btn-sm" onClick={()=>setShowPay(inv)}><DollarSign size={12}/> Pay</button>}
                        {inv.payment_status==='PAID'&&<button className="btn btn-outline btn-sm" onClick={async()=>{ const r=await financeApi.getInvoice(inv.invoice_id); }}><Printer size={12}/></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="invoice" title="No invoices found" />}
        </div>
        <Pagination page={page} pages={pagination.pages||1} onPage={setPage} />
      </div>

      <Modal open={!!selected} onClose={()=>{setSelected(null);setDetail(null);}} title="Invoice Detail" size="modal-lg">
        {detail ? (
          <>
            <div className="modal-body">
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16, padding:14, background:'var(--gray-50)', borderRadius:8 }}>
                <div>
                  <div style={{ fontSize:'.68rem', color:'var(--gray-400)', marginBottom:4 }}>BILL TO</div>
                  <div style={{ fontWeight:700 }}>{detail.invoice.customer_name}</div>
                  {detail.invoice.company_name&&<div className="text-sm">{detail.invoice.company_name}</div>}
                  <div className="text-sm text-muted">{detail.invoice.customer_email}</div>
                </div>
                <div style={{ fontSize:'.85rem' }}>
                  <div><span style={{ color:'var(--gray-400)', fontSize:'.72rem' }}>Date: </span>{fmtDate(detail.invoice.created_at)}</div>
                  <div><span style={{ color:'var(--gray-400)', fontSize:'.72rem' }}>Due: </span>{detail.invoice.due_date?fmtDate(detail.invoice.due_date):'On Receipt'}</div>
                  <div><span style={{ color:'var(--gray-400)', fontSize:'.72rem' }}>Status: </span><PaymentBadge status={detail.invoice.payment_status}/></div>
                </div>
              </div>
              <div className="table-wrap" style={{ marginBottom:12 }}>
                <table>
                  <thead><tr><th>Description</th><th>Qty</th><th>Unit Price</th><th>Disc%</th><th>Tax%</th><th>Amount</th></tr></thead>
                  <tbody>
                    {detail.lineItems.map(l=>(
                      <tr key={l.line_id}>
                        <td><div style={{ fontSize:'.85rem' }}>{l.description}</div>{l.tracking_id&&<code style={{ fontSize:'.72rem', color:'var(--navy)' }}>{l.tracking_id}</code>}</td>
                        <td>{l.quantity}</td><td>{fmtPKR(l.unit_price)}</td>
                        <td>{l.discount_pct>0?l.discount_pct+'%':'-'}</td>
                        <td>{l.tax_pct>0?l.tax_pct+'%':'-'}</td>
                        <td style={{ fontWeight:600 }}>{fmtPKR(l.line_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ maxWidth:260, marginLeft:'auto' }}>
                <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid var(--gray-100)', fontSize:'.82rem' }}><span>Subtotal</span><span>{fmtPKR(detail.invoice.subtotal)}</span></div>
                <div style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid var(--gray-100)', fontSize:'.82rem' }}><span>Tax / GST</span><span>{fmtPKR(detail.invoice.tax_amount)}</span></div>
                <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', fontWeight:700, borderTop:'2px solid var(--navy)', color:'var(--navy)' }}><span>Total Due</span><span style={{ fontSize:'1.1rem' }}>{fmtPKR(detail.invoice.total)}</span></div>
              </div>
            </div>
            <div className="modal-footer">
              {(detail.invoice.payment_status==='PENDING'||detail.invoice.payment_status==='OVERDUE')&&<button className="btn btn-success" onClick={()=>{setSelected(null);setDetail(null);setShowPay(detail.invoice);}}><DollarSign size={14}/> Record Payment</button>}
              <button className="btn btn-outline" onClick={()=>{setSelected(null);setDetail(null);}}>Close</button>
            </div>
          </>
        ) : <div style={{ padding:40, textAlign:'center' }}><Spinner /></div>}
      </Modal>

      <Modal open={showCreate} onClose={()=>{setShowCreate(false);setCreateForm(blankForm());}} title="Create Invoice" size="modal-lg">
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Customer <span className="req">*</span></label><CustomerSearch value={createForm.customerId} onChange={(id,obj)=>setCreateForm(f=>({...f,customerId:id,customerObj:obj}))} /></div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Payment Terms</label>
              <select className="form-control" value={createForm.paymentTerms} onChange={e=>setCreateForm(f=>({...f,paymentTerms:e.target.value}))}>
                <option value="IMMEDIATE">Due on receipt</option><option value="NET7">Net 7 days</option><option value="NET15">Net 15 days</option><option value="NET30">Net 30 days</option><option value="NET45">Net 45 days</option><option value="NET60">Net 60 days</option>
              </select>
            </div>
            <div className="form-group"><label className="form-label">Custom Due Date</label><input className="form-control" type="date" value={createForm.dueDate} onChange={e=>setCreateForm(f=>({...f,dueDate:e.target.value}))} /></div>
          </div>
          <div style={{ padding:'10px 14px', background:'var(--gray-50)', borderRadius:8, marginBottom:14, border:'1px solid var(--gray-200)' }}>
            <div style={{ fontSize:'.78rem', fontWeight:600, color:'var(--gray-600)', marginBottom:8 }}>Auto-pull postpaid shipments (optional)</div>
            <div className="form-row" style={{ margin:0 }}>
              <div className="form-group" style={{ margin:0 }}><label className="form-label" style={{ fontSize:'.75rem' }}>From</label><input className="form-control" type="date" value={createForm.billingPeriodStart} onChange={e=>setCreateForm(f=>({...f,billingPeriodStart:e.target.value}))} /></div>
              <div className="form-group" style={{ margin:0 }}><label className="form-label" style={{ fontSize:'.75rem' }}>To</label><input className="form-control" type="date" value={createForm.billingPeriodEnd} onChange={e=>setCreateForm(f=>({...f,billingPeriodEnd:e.target.value}))} /></div>
            </div>
          </div>
          <div style={{ marginBottom:10 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <div style={{ fontWeight:600, fontSize:'.85rem' }}>Line Items</div>
              <button className="btn btn-outline btn-sm" onClick={addLine}><Plus size={12}/> Add Line</button>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'var(--gray-50)' }}>
                <th style={{ padding:'6px 8px', textAlign:'left', border:'1px solid var(--gray-200)' }}>Description</th>
                <th style={{ padding:'6px 4px', textAlign:'center', border:'1px solid var(--gray-200)', width:64 }}>Qty</th>
                <th style={{ padding:'6px 4px', textAlign:'right', border:'1px solid var(--gray-200)', width:110 }}>Unit Price</th>
                <th style={{ padding:'6px 4px', textAlign:'center', border:'1px solid var(--gray-200)', width:62 }}>Disc%</th>
                <th style={{ padding:'6px 4px', textAlign:'center', border:'1px solid var(--gray-200)', width:62 }}>Tax%</th>
                <th style={{ padding:'6px 8px', textAlign:'right', border:'1px solid var(--gray-200)', width:100 }}>Amount</th>
                <th style={{ width:28, border:'1px solid var(--gray-200)' }}></th>
              </tr></thead>
              <tbody>{createForm.lines.map((line,i)=><LineRow key={i} line={line} index={i} onChange={setLine} onRemove={removeLine}/>)}</tbody>
            </table>
            <InvoiceTotals lines={createForm.lines} />
          </div>
          <div className="form-group"><label className="form-label">Notes</label><textarea className="form-control" rows={2} value={createForm.notes} onChange={e=>setCreateForm(f=>({...f,notes:e.target.value}))} /></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>{setShowCreate(false);setCreateForm(blankForm());}}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>{creating?'Creating...':'Create Invoice'}</button>
        </div>
      </Modal>

      {showPay && <MarkPaidModal invoice={showPay} onClose={()=>setShowPay(null)} onDone={()=>{setShowPay(null);load();}} />}
    </AppShell>
  );
}
